// Client-side multi-hop isochrone chaining so we can support travel times
// above ORS's 60-minute per-call cap (up to ~180 min).

import * as turf from '@turf/turf';
import { unionAll } from './compute.js';

const MAX_PER_CALL = 60;
const SAMPLES_PER_HOP = 8;

export async function computeIsochrone({ lon, lat, profile, minutes, onStatus }) {
  const mins = Math.max(1, Math.min(180, Number(minutes) || 0));

  // Single call — easy path.
  if (mins <= MAX_PER_CALL) {
    const gj = await callOrs({ seeds: [[lon, lat]], profile, minutes: mins });
    return gj.features?.[0] || null;
  }

  // Multi-hop. Start with MAX from origin.
  onStatus?.(`Hop 1 (60 min) from origin…`);
  let current = (await callOrs({ seeds: [[lon, lat]], profile, minutes: MAX_PER_CALL })).features?.[0];
  if (!current) return null;
  let remaining = mins - MAX_PER_CALL;
  let hopIndex = 1;

  while (remaining > 0) {
    hopIndex++;
    const hopMin = Math.min(MAX_PER_CALL, remaining);
    const seeds = sampleBoundary(current, SAMPLES_PER_HOP);
    if (!seeds.length) break;

    onStatus?.(`Hop ${hopIndex} (${hopMin} min) from ${seeds.length} boundary seeds…`);

    // ORS accepts up to 5 locations per call; batch.
    const newFeatures = [];
    for (let i = 0; i < seeds.length; i += 5) {
      const chunk = seeds.slice(i, i + 5);
      const gj = await callOrs({ seeds: chunk, profile, minutes: hopMin });
      newFeatures.push(...(gj.features || []));
    }

    const merged = unionAll([current, ...newFeatures]);
    if (!merged) break;
    current = merged;
    remaining -= hopMin;
  }

  return current;
}

async function callOrs({ seeds, profile, minutes }) {
  const res = await fetch('/api/isochrone', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ seeds, profile, minutes })
  });
  if (!res.ok) {
    const txt = await safeText(res);
    throw new Error(`Isochrone failed: ${res.status} ${txt}`);
  }
  return res.json();
}

async function safeText(r) { try { return (await r.text()).slice(0, 200); } catch { return ''; } }

function sampleBoundary(feature, n) {
  const geom = feature.geometry;
  if (!geom) return [];
  const rings = [];
  if (geom.type === 'Polygon') rings.push(geom.coordinates[0]);
  else if (geom.type === 'MultiPolygon') for (const p of geom.coordinates) rings.push(p[0]);
  if (!rings.length) return [];

  // Walk each ring by perimeter and pick evenly-spaced samples.
  const out = [];
  const perRing = Math.max(1, Math.floor(n / rings.length));
  for (const ring of rings) {
    try {
      const line = turf.lineString(ring);
      const len = turf.length(line, { units: 'kilometers' });
      if (!len) continue;
      for (let i = 0; i < perRing; i++) {
        const t = (i + 0.5) / perRing;
        const pt = turf.along(line, len * t, { units: 'kilometers' });
        const c = pt.geometry.coordinates;
        out.push([c[0], c[1]]);
      }
    } catch {}
  }
  return out;
}
